import { arrayUpdate, combineQueries, EntityActions, filterNilValue } from '@datorama/akita';
import { type GridDataSource } from '@mui/x-data-grid-premium';
import { formatISO, parseISO } from 'date-fns';
import { diff } from 'deep-object-diff';
import { isEqual, pick } from 'lodash-es';
import { distinctUntilChanged, distinctUntilKeyChanged, firstValueFrom, map, type Subscription, tap } from 'rxjs';
import { entities$, query } from './query';
import store, { type OrganisationsState } from './store';
import { activeOrganisationId$, organisationContextQuery, topLevelOrganisationId$ } from 'context/OrganisationContext/query';
import { context$ as periodContext$ } from 'context/PeriodContext/query';
import { includeSubsidiaries$ } from 'context/UserContext/query';
import userContextStore, { type UserContextState } from 'context/UserContext/store';
import CRUDService from 'state/CRUDService';
import KPISetService from 'state/KPISet/service';
import MinimumSafeguardsService from 'state/MinimumSafeguards/service';
import PeriodsService from 'state/Period/service';
import { query as uiQuery } from 'state/UI/query';
import UsersService from 'state/User/service';
import { type Organisation, type OrganisationEmissionResult, type Period, type ReportingStandard, type Tag } from 'types/models';
import { canAccessOrganisation } from 'utils';
import { isOrganisationValidForPeriod } from 'utils/organisationValidity';
export default class OrganisationsService extends CRUDService<OrganisationsState> {
   private static instance: OrganisationsService;
   private statisticsQueryParamsObservable = this.getStatisticsQueryParams();
   private usersService = new UsersService();
   private userContextStore = userContextStore;
   private kpiSetService = new KPISetService();
   private periodService = new PeriodsService();
   private minimumSafeguardsService = new MinimumSafeguardsService();
   private statisticsSubscription?: Subscription;
   private emissionResultSubscription?: Subscription;
   private lockStatusSubscription?: Subscription;
   private statisticsController?: AbortController;

   constructor() {
      if (!OrganisationsService.instance) {
         super('organisations', store, query, {
            usePeriodContext: true,
            useScope: false,
            includeSubsidiaries: false,
         });
         this.getLockStatus();
         this.statisticsQueryParamsObservable = this.getStatisticsQueryParams();
         OrganisationsService.instance = this;
      }

      // Service shall be instantiated only once, because otherwise the observable will be created for each service instance

      return OrganisationsService.instance;
   }

   getEmissionResults(force = false) {
      if (force && this.emissionResultSubscription && !this.emissionResultSubscription.closed) {
         this.emissionResultSubscription.unsubscribe();
      }

      if (!this.emissionResultSubscription || this.emissionResultSubscription?.closed) {
         this.emissionResultSubscription = combineQueries([
            activeOrganisationId$.pipe(filterNilValue(), distinctUntilChanged()),
            periodContext$.pipe(filterNilValue(), distinctUntilKeyChanged('id')),
            includeSubsidiaries$.pipe(filterNilValue(), distinctUntilChanged()),
         ])
            .pipe(
               distinctUntilChanged(isEqual),
               tap(
                  ([organisationId, { from, to }, includeSubsidiaries]: [Organisation['id'], Period, boolean]) =>
                     void this.updateOrganisationResults(organisationId, from, to, includeSubsidiaries)
               )
            )
            .subscribe();
      }
   }

   getLockStatus(force = false) {
      if (force && this.lockStatusSubscription && !this.lockStatusSubscription.closed) {
         this.lockStatusSubscription.unsubscribe();
      }

      if (!this.lockStatusSubscription || this.lockStatusSubscription?.closed) {
         this.lockStatusSubscription = combineQueries([
            periodContext$,
            topLevelOrganisationId$,
            this.query.selectEntityAction([EntityActions.Set, EntityActions.Update]),
         ])
            .pipe(
               filterNilValue(),
               distinctUntilChanged(isEqual),
               tap(
                  ([periodContext, topLevelOrganisationId]) =>
                     periodContext &&
                     this.periodService
                        .getOrganisationLockStatus(periodContext.id)
                        .then((statuses) => {
                           const organisations = this.store.getValue().entities;

                           const lockStatusUpdate = statuses
                              .filter((status) => organisations?.[status.organisationId])
                              .map((status) => {
                                 const { organisationId } = status;
                                 const organisation = organisations?.[organisationId];

                                 const statusCopy = {
                                    id: organisationId,
                                    lockStatus: { ...pick(status, ['allowDataEntry', 'closingHubOpen', 'periodOpen']), active: true },
                                 };

                                 if (
                                    !canAccessOrganisation(organisationId) ||
                                    organisation?.deletedAt ||
                                    (organisation?.id !== topLevelOrganisationId &&
                                       !isOrganisationValidForPeriod(organisation?.parents, periodContext?.from, periodContext?.to))
                                 ) {
                                    statusCopy.lockStatus.allowDataEntry = false;
                                 }

                                 if (
                                    organisation?.id !== topLevelOrganisationId &&
                                    !isOrganisationValidForPeriod(organisation?.parents, periodContext?.from, periodContext?.to)
                                 ) {
                                    statusCopy.lockStatus.active = false;
                                 }

                                 return statusCopy;
                              }) as unknown as Organisation[];

                           return this.store.upsertMany(lockStatusUpdate);
                        })
                        .catch(() => {
                           // Handle error
                        })
               )
            )
            .subscribe();
      }
   }

   async updateOrganisationContextIfModified(entityId: Organisation['id']) {
      const organisationId = organisationContextQuery.getValue()?.organisation?.id;
      const activeOrganisationId = organisationContextQuery.getValue()?.activeOrganisation?.id;
      if ([organisationId, activeOrganisationId].includes(entityId)) {
         return this.usersService.getLoggedInUser(true);
      }
   }

   async updateEntity(
      entityId: Organisation['id'],
      changes: Partial<Organisation>,
      updateStore = true,
      store = this.store,
      options: { throwOnError?: boolean } = {}
   ) {
      const result = await super.updateEntity(entityId, changes, updateStore, store, options);
      await this.updateOrganisationContextIfModified(entityId);
      return result;
   }

   async createEntity(entity: Omit<Organisation, 'industries'>, fetchAssociations = false) {
      const result = await super.createEntity(entity, fetchAssociations);
      await this.usersService.getLoggedInUser(true); // Force refresh
      return result;
   }

   async deleteEntity(entityIdOrArray: Organisation['id'] | Organisation['id'][], targetOrganisationIdForUsersMove?: Organisation['id'] | null) {
      if (Array.isArray(entityIdOrArray)) {
         return super.deleteEntity(entityIdOrArray);
      }

      const entity = super.getEntityState(entityIdOrArray);

      if (entity?.deleteOperationInProgress) return;

      this.store.update(entity?.id, { deleteOperationInProgress: true, deleteOperationStatus: 'PENDING' });

      const queryParams = new URLSearchParams();

      if (targetOrganisationIdForUsersMove) {
         queryParams.set('targetOrganisationId', String(targetOrganisationIdForUsersMove));
      }

      return this.httpClient
         .delete(`/${this.version}/${this.entityName}/${entityIdOrArray}?${queryParams.toString()}`)
         .then(() => {
            this.store.upsert(entity?.id, {
               deletedAt: new Date().toISOString(),
               deleteOperationInProgress: false,
               deleteOperationStatus: 'SUCCESS',
               name: entity?.name,
               code: entity?.code,
            });
            return null as any;
         })
         .catch(() => {
            this.store.upsert(entity?.id, {
               deleteOperationInProgress: false,
               deleteOperationStatus: 'ERROR',
               name: entity?.name,
               code: entity?.code,
            });
         });
   }

   #updatePropertyInCurrentOrganisation(propertyName: string, value: any) {
      return this.userContextStore.update((state: UserContextState) => ({
         ...state,
         activeOrganisation: {
            ...state.activeOrganisation,
            [propertyName]: value,
         },
      }));
   }

   async updateOrganisationResults(organisationId: Organisation['id'], from: Period['from'], to: Period['to'], includeSubsidiaries: boolean) {
      const queryParams = new URLSearchParams();

      if (organisationId) {
         queryParams.set('organisationId', String(organisationId));
      }
      if (from) {
         queryParams.set('from', from);
      }
      if (to) {
         queryParams.set('to', to);
      }
      if (includeSubsidiaries) {
         queryParams.set('includeSubsidiaries', String(includeSubsidiaries));
      }

      if (organisationId) {
         return this.httpClient
            .get(`/${this.version}/${this.entityName}/${organisationId}/results?${queryParams.toString()}`)
            .then((resp) => this.#updatePropertyInCurrentOrganisation('results', resp.data))
            .catch((error) => this.setError(error));
      }

      return false;
   }

   /**
    * Fetches emission results for a specific period without updating global state.
    * Used by the reduction plan to fetch results for multiple periods in parallel.
    */
   async fetchEmissionResultsForPeriod(
      organisationId: Organisation['id'],
      from: string,
      to: string,
      includeSubsidiaries: boolean
   ): Promise<OrganisationEmissionResult[]> {
      if (!organisationId) {
         return [];
      }

      try {
         const queryParams = new URLSearchParams();
         queryParams.set('organisationId', String(organisationId));

         if (from) {
            queryParams.set('from', from);
         }
         if (to) {
            queryParams.set('to', to);
         }
         if (includeSubsidiaries) {
            queryParams.set('includeSubsidiaries', String(includeSubsidiaries));
         }

         const resp = await this.httpClient.get(`/${this.version}/${this.entityName}/${organisationId}/results?${queryParams.toString()}`);
         return resp.data ?? [];
      } catch (error) {
         this.setError(error as Record<string, unknown>);
         return [];
      }
   }

   async addReportingStandards(reportingStandardIds: ReportingStandard['id'][]) {
      const context = organisationContextQuery.getValue();

      if (context?.activeOrganisation?.id) {
         return this.httpClient
            .post(`/${this.version}/${this.entityName}/${context?.activeOrganisation?.id}/reportingstandards`, {
               reportingStandards: reportingStandardIds,
            })
            .then((resp) => {
               this.#updatePropertyInCurrentOrganisation('reportingStandards', resp.data);
               return this.kpiSetService.getEntities({ force: true });
            })
            .catch((error) => this.setError(error));
      }

      return false;
   }

   async removeReportingStandard(reportingStandardId: ReportingStandard['id']) {
      const context = organisationContextQuery.getValue();

      if (context?.activeOrganisation?.id) {
         return this.httpClient
            .delete(`/${this.version}/${this.entityName}/${context?.activeOrganisation?.id}/reportingstandards/${reportingStandardId}`)
            .then((resp) => {
               this.#updatePropertyInCurrentOrganisation('reportingStandards', resp.data);
               return this.kpiSetService.getEntities({ force: true });
            })
            .catch((error) => this.setError(error));
      }
      return false;
   }

   async changeReportingStandardAssignment(reportingStandardId: ReportingStandard['id'], changes: Partial<ReportingStandard>) {
      const context = organisationContextQuery.getValue();

      if (context?.activeOrganisation?.id) {
         return this.httpClient
            .patch(`/${this.version}/${this.entityName}/${context?.activeOrganisation?.id}/reportingstandards/${reportingStandardId}`, changes)
            .then((resp) => {
               this.userContextStore.update((state: UserContextState) => ({
                  ...state,
                  activeOrganisation: {
                     ...state.activeOrganisation,
                     reportingStandards: arrayUpdate(state?.activeOrganisation?.reportingStandards ?? [], reportingStandardId, resp.data),
                  },
               }));

               return this.kpiSetService.getEntities({ force: true });
            })
            .catch((error) => this.setError(error));
      }

      return false;
   }

   getStatisticsQueryParams() {
      const dependencies = [];

      dependencies.push(activeOrganisationId$.pipe(filterNilValue()));
      dependencies.push(periodContext$.pipe(filterNilValue()));
      dependencies.push(includeSubsidiaries$.pipe(filterNilValue()));
      dependencies.push(uiQuery.select('statisticsFilters'));

      return combineQueries(dependencies).pipe(
         distinctUntilChanged(isEqual),
         map(
            ([organisationId, periodContext, includeSubsidiaries, filters]: [
               Organisation['id'],
               Period,
               boolean,
               // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
               Array<Record<'periods' | 'reportingStandards' | 'organisations' | 'tags' | any, any>>,
            ]) => {
               const urlParamsSubClass = new URLSearchParams();

               if (this.useScope === true) {
                  urlParamsSubClass.set('scope', String(true));
               } else if (this.useScope === false) {
                  urlParamsSubClass.set('scope', String(false));
               } else {
                  urlParamsSubClass.set('scope', 'any');
               }

               if (organisationId) {
                  urlParamsSubClass.set('organisationId', String(organisationId));
               }

               if (periodContext) {
                  urlParamsSubClass.set('from', formatISO(parseISO(periodContext.from), { representation: 'date' }));
                  urlParamsSubClass.set('to', formatISO(parseISO(periodContext.to), { representation: 'date' }));
               }

               if (includeSubsidiaries) {
                  urlParamsSubClass.set('includeSubsidiaries', String(includeSubsidiaries));
               }

               if (filters) {
                  const groupedFilter = Object.entries(filters).reduce((acc, [kind, filterItems = []]) => {
                     const formatData = (date: Date | string) =>
                        formatISO(typeof date === 'string' ? parseISO(date) : date, { representation: 'date' });
                     switch (kind) {
                        case 'periods':
                           return {
                              ...acc,
                              ...[filterItems]
                                 .flat()
                                 .filter((item) => !!item)
                                 .map(({ from, to }) => ({ from: formatData(from), to: formatData(to) })),
                           };
                        case 'reportingStandards':
                           return {
                              ...acc,
                              reportingStandardId: filterItems.map((filter: ReportingStandard) => filter?.id),
                           };
                        case 'organisations':
                           return {
                              ...acc,
                              organisationId: filterItems.map((filter: Organisation) => filter?.id),
                           };
                        case 'tags':
                           return {
                              ...acc,
                              tag: filterItems.map((filter: Tag) => filter?.name),
                           };
                        default:
                           return acc;
                     }
                  }, {});

                  for (const [key, value] of Object.entries(groupedFilter)) {
                     if (Array.isArray(value) && value.length) {
                        urlParamsSubClass.set(key, [value].flat().join(','));
                     }
                  }
               }

               if (periodContext && organisationId) {
                  return `?${urlParamsSubClass.toString()}`;
               }

               return undefined;
            }
         )
      );
   }

   async getStatistics() {
      if (this.statisticsSubscription === undefined || this.statisticsSubscription?.closed) {
         this.statisticsSubscription = this.getStatisticsQueryParams()
            .pipe(filterNilValue(), distinctUntilChanged())
            .subscribe((queryString) => {
               if (typeof queryString === 'string') {
                  if (this.statisticsController) {
                     this.statisticsController.abort();
                  }

                  this.statisticsController = new AbortController();

                  this.store.setLoading(true);
                  this.httpClient
                     .get(`/${this.version}/statistics/emissionsinfo${queryString}`, {
                        signal: this.statisticsController.signal,
                     })
                     .then((resp) => {
                        this.store.update({ statistics: resp.data });
                        return this.store.setLoading(false);
                     })
                     .catch((error) => {
                        if (!(error?.name === 'AbortError' || error?.code === 'ERR_CANCELED')) {
                           return this.setError(error);
                        }
                        return this.store.setLoading(false);
                     });
               }

               return undefined;
            });
      }
   }

   async setTags(organisationId: Organisation['id'], tags: string[] = []) {
      this.httpClient
         .put(`/${this.version}/${this.entityName}/${organisationId}/tags`, tags)
         .then((resp) => this.store.update(organisationId, { tags: resp.data }))
         .catch((error) => this.setError(error));
   }

   async setIndustries(organisationId: Organisation['id'], industries = []) {
      this.httpClient
         .put(`/${this.version}/${this.entityName}/${organisationId}/industries`, industries)
         .then((resp) => this.store.update(organisationId, { industries: resp.data }))
         .catch((error) => this.setError(error));
   }

   async setCharacteristics(organisationId: Organisation['id'], characteristics: number[] = []) {
      this.httpClient
         .put(`/${this.version}/${this.entityName}/${organisationId}/characteristics`, characteristics)
         .then((resp) => {
            this.store.update(organisationId, { characteristics: resp.data });
            this.minimumSafeguardsService.getStatus();
            return this.userContextStore.update((context) => ({
               ...context,
               activeOrganisation: {
                  ...context.activeOrganisation,
                  characteristics: resp.data,
               },
            }));
         })
         .catch((error) => this.setError(error));
   }

   async resetTheme(organisationId: Organisation['id']) {
      return this.httpClient
         .patch(`/${this.version}/${this.entityName}/${organisationId}`, {
            theme: null,
            logo: null,
            banner: null,
            background: null,
         })
         .then((resp) => {
            if (this.query.hasEntity(organisationId)) {
               this.store.update(organisationId, resp.data);
            }
            this.#updatePropertyInCurrentOrganisation('logo', null);
            this.#updatePropertyInCurrentOrganisation('banner', null);
            this.#updatePropertyInCurrentOrganisation('background', null);
            return this.#updatePropertyInCurrentOrganisation('theme', null);
         })
         .catch((error) => {
            this.setError(error);
         });
   }

   async #uploadFile(organisationId: Organisation['id'], files: File[], propertyName: string) {
      files.forEach((file) => {
         const formData = new FormData();
         formData.append(propertyName, file);

         this.httpClient
            .post(`/${this.version}/${this.entityName}/${organisationId}/${propertyName}`, formData, {
               headers: { 'Content-Type': 'multipart/form-data' },
            })
            .then((resp) => {
               if (this.query.hasEntity(organisationId)) {
                  this.store.update(organisationId, resp.data);
               }
               return this.#updatePropertyInCurrentOrganisation(propertyName, resp.data[propertyName]);
            })
            .catch((error) => {
               this.setError(error);
            });
      });
   }

   async uploadLogo(organisationId: Organisation['id'], files: File[]) {
      return this.#uploadFile(organisationId, files, 'logo');
   }

   async uploadBanner(organisationId: Organisation['id'], files: File[]) {
      return this.#uploadFile(organisationId, files, 'banner');
   }

   async uploadBackground(organisationId: Organisation['id'], files: File[]) {
      return this.#uploadFile(organisationId, files, 'background');
   }

   async uploadFavIcon(organisationId: Organisation['id'], files: File[]) {
      return this.#uploadFile(organisationId, files, 'favicon');
   }
   async restore(organisationId: Organisation['id']) {
      return this.httpClient
         .post(`/${this.version}/${this.entityName}/${organisationId}/restore`)
         .then((resp) => this.store.update(organisationId, resp.data))
         .catch((error) => this.setError(error));
   }

   async updateCache() {
      return this.httpClient
         .post(`/${this.version}/${this.entityName}/updatecache`)
         .then(() => this.getEntities({ force: true }))
         .catch((error) => this.setError(error));
   }

   unsubscribeStatistics() {
      if (this.statisticsSubscription && !this.statisticsSubscription.closed) {
         this.statisticsSubscription.unsubscribe();
      }
      if (this.statisticsController) {
         this.statisticsController.abort();
      }
   }

   unsubscribeEmissionResults() {
      if (this.emissionResultSubscription && !this.emissionResultSubscription.closed) {
         this.emissionResultSubscription.unsubscribe();
      }
   }

   buildDataSource(): GridDataSource {
      return {
         getRows: async () => {
            await firstValueFrom(entities$);

            const rows = this.query.getAll();

            return {
               rows,
               rowCount: rows.length,
            };
         },
         getAggregatedValue: (row, field) => row[field],
         updateRow: async (params) => this.updateEntity(params.rowId as number, diff(params.previousRow, params.updatedRow)),
      };
   }
}
